import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Chip,
  DataList,
  EmptyState,
  ErrorState,
  Nota,
  PageHeader,
  Pagination,
  SearchInput,
  Skeleton,
} from '../../../ui';

import ModalFiltrosUsuarios, {
  FILTROS_USUARIOS_PADRAO,
  FiltrosUsuarios,
  contarFiltrosUsuariosAtivos,
} from '../../../components/modal/filtros-usuarios/ModalFiltrosUsuarios';
import ModalImportarUsuariosCsv from '../../../components/modal/ti/ModalImportarUsuariosCsv';

import { TI, TIUSUARIO, TIUSUARIO_NOVO } from '../routes';
import {
  TotaisUsuarios,
  UsuarioListaItem,
  listarUsuarios,
} from '../../../api/user/userApi';
import { formatCpfCnpj } from '../../../utils/formartCPF';
import { rotuloPapel, rotuloSituacao, varianteDoPapel } from '../../../utils/usuarioRegras';
import { extrairMensagemErro } from '../../../utils/apiError';

/**
 * Central de administração de usuários — a LISTA (at-68, frente D).
 *
 * ## O que esta tela substitui
 *
 * Duas telas legadas: a `/ti/usuarios` antiga (575 linhas, com a lista e o formulário de
 * edição no mesmo componente, alternados por um `useState`) e a `/ti/criar-usuario` (369
 * linhas, o mesmo formulário outra vez). Criar e editar passaram a ser a mesma ficha, em
 * sub-rota (`/ti/usuarios/novo` e `/ti/usuarios/:id`).
 *
 * ## O que mudou de comportamento
 *
 *   - **Busca de verdade**: além do nome (sem acento), casa e-mail, CPF por dígitos e
 *     código de voluntário. Antes era só nome completo.
 *   - **Filtros** de papel e situação, em modal com rascunho (padrão at-28/at-65).
 *   - **Contadores globais** no topo — total, ativos, inativos e primeiro acesso pendente.
 *   - **Situação na lista**: a coluna existe porque desativar usuário passou a existir.
 *   - **Erro deixou de ser mudo** e o vazio distingue três casos.
 *   - **Colapsa em cards no celular** (`DataList`), em vez da `<table>` que transbordava.
 *
 * ⚠️ O filtro de situação nasce em **ATIVOS**. É por isso que o vazio por filtro lembra
 * que inativos estão fora do recorte: sem isso, "não acho o fulano" leva à conclusão
 * errada de que ele nunca existiu.
 */

const LIMITE_POR_PAGINA = 10;

type EstadoLista = 'carregando' | 'ok' | 'erro';

const UsuariosPage: React.FC = () => {
  const navigate = useNavigate();
  const voltar = useVoltar(TI());

  const [itens, setItens] = useState<UsuarioListaItem[]>([]);
  const [totais, setTotais] = useState<TotaisUsuarios | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [busca, setBusca] = useState('');
  const [filtros, setFiltros] = useState<FiltrosUsuarios>(FILTROS_USUARIOS_PADRAO);
  const [estado, setEstado] = useState<EstadoLista>('carregando');
  const [erro, setErro] = useState('');

  const [filtrosAberto, setFiltrosAberto] = useState(false);
  const [importacaoAberta, setImportacaoAberta] = useState(false);
  const [chaveBusca, setChaveBusca] = useState(0);

  const filtrosAtivos = contarFiltrosUsuariosAtivos(filtros);

  const carregar = useCallback(async () => {
    setEstado('carregando');
    try {
      const resposta = await listarUsuarios({
        busca: busca || undefined,
        papel: filtros.papel || undefined,
        situacao: filtros.situacao,
        page,
        limit: LIMITE_POR_PAGINA,
      });

      setItens(resposta.items ?? []);
      setTotais(resposta.totais ?? null);
      setTotalItems(resposta.totalItems ?? 0);
      setEstado('ok');
    } catch (falha) {
      // 🔴 A lista é DESCARTADA no erro: manter a anterior faria dado velho passar por
      // atual, e aqui o TI decide sobre acesso a partir do que vê (lição da at-66).
      setItens([]);
      setErro(extrairMensagemErro(falha, 'Não foi possível carregar os usuários.'));
      setEstado('erro');
    }
  }, [busca, filtros.papel, filtros.situacao, page]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const trocarBusca = (termo: string) => {
    setBusca(termo);
    setPage(1);
  };

  const aplicarFiltros = (novos: FiltrosUsuarios) => {
    setFiltros(novos);
    setPage(1);
  };

  const limparBusca = () => {
    setBusca('');
    setPage(1);
    setChaveBusca(c => c + 1);
  };

  const colunas = [
    {
      id: 'nome',
      rotulo: 'Nome',
      principal: true,
      celula: (u: UsuarioListaItem) => u.nomeCompleto || 'Sem nome cadastrado',
    },
    {
      id: 'email',
      rotulo: 'E-mail',
      secundaria: true,
      celula: (u: UsuarioListaItem) => u.email,
    },
    {
      id: 'codigo',
      rotulo: 'Código',
      secundaria: true,
      celula: (u: UsuarioListaItem) => (u.volunteerCode ? `Código ${u.volunteerCode}` : 'Sem código'),
    },
    {
      id: 'cpf',
      rotulo: 'CPF / CNPJ',
      ocultarNoCard: true,
      celula: (u: UsuarioListaItem) => (u.cpfCnpj ? formatCpfCnpj(u.cpfCnpj) : '—'),
    },
    {
      id: 'papel',
      rotulo: 'Papel',
      celula: (u: UsuarioListaItem) => (
        <Chip variante={varianteDoPapel(u.userType)}>{rotuloPapel(u.userType)}</Chip>
      ),
    },
    {
      id: 'situacao',
      rotulo: 'Situação',
      celula: (u: UsuarioListaItem) => {
        const situacao = rotuloSituacao(u);
        return <Chip variante={situacao.variante}>{situacao.texto}</Chip>;
      },
    },
    {
      id: 'vinculos',
      rotulo: 'Vínculos',
      ocultarNoCard: true,
      celula: (u: UsuarioListaItem) => (
        <span className="tw-text-xs tw-text-texto-suave">
          {u.regionais.length} regional(is) · {u.cooperativas.length} cooperativa(s)
        </span>
      ),
    },
  ];

  const conteudo = () => {
    if (estado === 'carregando') return <Skeleton linhas={5} altura="tw-h-14" />;

    if (estado === 'erro') {
      return (
        <ErrorState
          mensagem="Não foi possível carregar os usuários."
          detalhe={erro}
          aoTentarNovamente={() => void carregar()}
        />
      );
    }

    if (itens.length === 0) {
      // Os três vazios pedem ações diferentes (spec 02 §2.7).
      if (busca) {
        return (
          <EmptyState
            icone="busca"
            titulo="Nenhum usuário encontrado para essa busca"
            descricao={`A busca cobre nome, e-mail, CPF e código de voluntário. Nada corresponde a "${busca}".${
              filtros.situacao === 'ATIVOS' ? ' Usuários inativos estão fora do recorte atual.' : ''
            }`}
            acao={
              <Botao variante="secundario" onClick={limparBusca}>
                Limpar a busca
              </Botao>
            }
          />
        );
      }

      if (filtrosAtivos > 0) {
        return (
          <EmptyState
            titulo="Nenhum usuário com esses filtros"
            descricao="Nenhum cadastro casa com o recorte escolhido."
            acao={
              <Botao variante="secundario" onClick={() => aplicarFiltros(FILTROS_USUARIOS_PADRAO)}>
                Limpar os filtros
              </Botao>
            }
          />
        );
      }

      return (
        <EmptyState
          icone="sem-dados"
          titulo="Nenhum usuário cadastrado"
          descricao="Crie o primeiro usuário ou importe uma planilha com vários de uma vez."
          acao={
            <Botao onClick={() => navigate(TIUSUARIO_NOVO())}>Novo usuário</Botao>
          }
        />
      );
    }

    return (
      <>
        <DataList
          itens={itens}
          colunas={colunas}
          chave={u => u.id}
          rotuloAcoes="Abrir"
          rotuloAcessivel="Usuários do sistema"
          acoes={u => (
            <Botao
              variante="secundario"
              tamanho="sm"
              onClick={() => navigate(TIUSUARIO(u.id))}
              className="tw-w-full md:tw-w-auto"
            >
              Abrir
            </Botao>
          )}
        />

        <div className="tw-mt-4">
          <Pagination
            pagina={page}
            totalItens={totalItems}
            porPagina={LIMITE_POR_PAGINA}
            aoTrocar={setPage}
          />
        </div>
      </>
    );
  };

  return (
    <ShellSaac
      cabecalho={
        <PageHeader
          titulo="Usuários"
          sobretitulo="Administração (TI)"
          aoVoltar={voltar}
          acaoDireita={
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              <Botao
                variante="secundario"
                tamanho="md"
                iconeEsquerda="documento"
                onClick={() => setImportacaoAberta(true)}
              >
                Importar CSV
              </Botao>
              <Botao
                tamanho="md"
                iconeEsquerda="usuario-mais"
                onClick={() => navigate(TIUSUARIO_NOVO())}
              >
                Novo usuário
              </Botao>
            </div>
          }
        />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-6xl md:tw-p-6">
          <div className="tw-bg-white tw-p-4 md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-p-6 md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-flex tw-flex-col tw-gap-4">
              <Nota>
                Aqui você cadastra, edita, redefine senha, encerra sessões e{' '}
                <strong>desativa o acesso</strong> de um usuário. Desativar não apaga nada:
                a carteira dele continua ligada ao código de voluntário.
              </Nota>

              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                <SearchInput
                  key={chaveBusca}
                  valorInicial={busca}
                  placeholder="Buscar por nome, e-mail, CPF ou código"
                  rotuloAcessivel="Buscar usuário"
                  className="tw-min-w-0 tw-flex-1 tw-basis-64"
                  aoBuscar={trocarBusca}
                />

                <Botao
                  variante="secundario"
                  tamanho="md"
                  iconeEsquerda="filtro"
                  aria-pressed={filtrosAtivos > 0}
                  onClick={() => setFiltrosAberto(true)}
                >
                  {filtrosAtivos > 0 ? `Filtros (${filtrosAtivos})` : 'Filtros'}
                </Botao>
              </div>

              {totais && (
                <div>
                  {/* Os totais são GLOBAIS e não seguem busca nem filtro (convenção
                      at-10/at-28). O aviso vai em texto, não em `title` — que não
                      existe no celular (at-65). */}
                  <p className="tw-m-0 tw-mb-1.5 tw-text-[11px] tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
                    Totais do sistema — não mudam com a busca nem com os filtros
                  </p>
                  <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                    <Chip variante="neutro">{totais.total} usuário(s)</Chip>
                    <Chip variante="ok">{totais.ativos} ativo(s)</Chip>
                    {totais.inativos > 0 && (
                      <Chip variante="neutro">{totais.inativos} inativo(s)</Chip>
                    )}
                    {totais.primeiroAcessoPendente > 0 && (
                      <Chip variante="pendente">
                        {totais.primeiroAcessoPendente} com primeiro acesso pendente
                      </Chip>
                    )}
                  </div>
                </div>
              )}

              {conteudo()}
            </div>
          </div>
        </div>
      </div>

      <ModalFiltrosUsuarios
        aberto={filtrosAberto}
        valor={filtros}
        onAplicar={aplicarFiltros}
        onFechar={() => setFiltrosAberto(false)}
      />

      <ModalImportarUsuariosCsv
        aberto={importacaoAberta}
        aoFechar={() => setImportacaoAberta(false)}
        aoImportar={() => void carregar()}
      />
    </ShellSaac>
  );
};

export default UsuariosPage;
